import { FC, useMemo, useState } from "react";

import {
  cellImageUrls,
  renderCellText,
} from "@/components/questions/editors/variableHighlight";
import { HStack, Media, MediaGallery, Text, VStack } from "@/components/ui";
import { Cell, Question, VariableDef } from "@/types/resources";

interface QuestionHeaderProps {
  /** Active rotation cell: when provided with `variables`, its text variables
      are substituted into the question text and the images the question
      references shown below. */
  cell?: Cell | null;
  question: Question;
  /** Optional section label rendered above the question text. */
  sectionTitle?: string | null;
  /** Section variables backing the `{{placeholders}}` in the question text. */
  variables?: VariableDef[];
}

/**
 * Presentational question header — order number and text (plus an optional
 * section label), with media shown underneath. The question's own variables,
 * plus the section's when a `cell` + `variables` are given (the detailed
 * results view), have their `{{tokens}}` replaced by the cell values
 * (highlighted) and the cell images shown alongside the question's own media.
 * Tapping a thumbnail opens the lightbox.
 */
const QuestionHeader: FC<QuestionHeaderProps> = ({
  cell,
  question,
  sectionTitle,
  variables,
}) => {
  // Lightbox for the shown media; `galleryIndex < 0` means closed.
  const [galleryIndex, setGalleryIndex] = useState(-1);

  // Mirrors the simulation's scoping: the question's own first cell shadows
  // the section cell on name collision. A self-rotating question has no single
  // cell to pipe.
  const scope = useMemo(() => {
    const ownCell = question.config?.cells_per_respondent
      ? null
      : (question.cells?.[0] ?? null);
    const byName = new Map<string, VariableDef>();
    [
      ...(cell ? (variables ?? []) : []),
      ...(ownCell ? (question.config?.variables ?? []) : []),
    ].forEach((v) => byName.set(v.name, v));
    return {
      cell: { ...cell, ...ownCell },
      variables: [...byName.values()],
    };
  }, [cell, question.cells, question.config, variables]);

  const substitute = scope.variables.length > 0;

  const media = useMemo(() => {
    const own = (question.media_urls ?? []).filter(Boolean);
    const fromCell = substitute
      ? cellImageUrls(
          [question.text, ...(question.options ?? [])],
          scope.variables,
          scope.cell
        )
      : [];
    // Attach each image once, preserving first-seen order (a cell image and
    // the question's own media can resolve to the same URL).
    return [...new Set([...fromCell, ...own])];
  }, [question.media_urls, question.options, question.text, scope, substitute]);

  return (
    <VStack className="gap-1">
      {sectionTitle ? (
        <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
          {sectionTitle}
        </Text>
      ) : null}
      <Text className="text-base text-typography-900 leading-relaxed">
        <Text className="font-semibold">{question.order}. </Text>
        {substitute
          ? renderCellText(question.text, scope.variables, scope.cell)
          : question.text}
      </Text>

      {media.length > 0 && (
        <HStack className="flex-wrap items-center gap-2 mt-2">
          {media.map((url, i) => (
            <Media
              key={i}
              alt={question.text}
              height={80}
              onPress={() => setGalleryIndex(i)}
              source={url}
            />
          ))}
        </HStack>
      )}

      <MediaGallery
        initialIndex={Math.max(0, galleryIndex)}
        media={media}
        onClose={() => setGalleryIndex(-1)}
        visible={galleryIndex >= 0}
      />
    </VStack>
  );
};

export default QuestionHeader;
